<template>
  <div class="gallery-wrapper">
    <Header ref="header" helpURL="studio/datasets/gallery/">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
    </Header>

    <!-- Gallery Header with Tabs and Pagination -->
    <GalleryHeader
      :seqCount="seqCount"
      :imageCount="filteredImageCount"
      :currentPage="currentPage"
      @update:currentPage="currentPage = $event"
      :selectedTab="activeTab"
      @update:selectedTab="activeTab = $event"
      :imagesPerPage="imagesPerPage"
      :permissions="permissions"
      :imageDisplaySettings="imageDisplaySettings"
      @update:imageDisplaySettings="imageDisplaySettings = $event"
      :annotationDisplaySettings="annotationDisplaySettings"
      @update:annotationDisplaySettings="annotationDisplaySettings = $event"
      :annotationSets="annotationSets"
      :currentLabels="currentLabels"
      :selectedImages="selectedImages"
      :selectAllFiles="selectAllFiles"
      :totalImageCount="filteredImageCount"
      :imageList="imageList"
      :currentDataset="currentDataset"
      :isPublic="isPublic"
      :hasSequences="hasSequences"
      :activeFilterCount="activeFilterCount"
      @delete-sequences="openConfirmDeleteSequencesModal"
      @delete-images="openConfirmDeleteImageModal"
      @toggle-filter="showFilterDialog = !showFilterDialog"
      @remove-tag="handleRemoveTagsForSelectedImages"
      @add-tag="handleAddTagsForSelectedImages"
      @refresh-dataset="handleRefreshPage"
      @copy-modal="showCopyModal = true"
      @import-clicked="openUploadModal"
      @select-all-page="handleSelectAllPageFiles"
      @select-all="handleSelectAllFiles"
      @clear-selection="handleClearSelectAllFiles"
    />

    <!-- Gallery Grid Content -->
    <div class="gallery-container">
      <!-- Sequence List -->
      <GallerySeqList
        v-if="activeTab === 'sequences'"
        :seqList="seqList"
        :modelValue="selectedImages"
        :datasetId="dsID"
        :annotationDisplaySettings="annotationDisplaySettings"
        :imageDisplaySettings="imageDisplaySettings"
        :filterParams="getImagesFilterParams()"
        @update:modelValue="selectedImages = $event"
        @sequence-deleted="handleSequenceDeleted"
        @sequence-edit="handleSequenceEdit"
        @sequence-download="handleSequenceDownload"
      />

      <!-- Image List -->
      <GalleryImageList
        v-if="activeTab === 'images' && dsID"
        :currentPage="currentPage"
        :imagesPerPage="imagesPerPage"
        :filter="datasetFilters"
        :datasetId="dsID"
        :annotationSets="annotationSets"
        :colorAnnotationsBy="colorAnnotationsBy"
        :displayAnnotationSets="displayAnnotationSets"
        :displayLabels="displayLabels"
        :currentLabels="currentLabels"
        :showImageName="showImageName"
        :showLabels="showLabels"
        :shouldShowSelector="true"
        :selectedImages="selectedImages"
        :selectAllFiles="selectAllFiles"
        :selectAllPageFiles="selectAllPageFiles"
        :gridItemDisplayProp="gridItemDisplayProp"
        :imagesCount="imageCount"
        @grid-item-clicked="displayImage"
        @update:selected="({ image, isSelected }) => toggleImageSelection(image, isSelected)"
      />
    </div>

    <!-- Filter Drawer Teleport -->
    <Teleport to="body">
      <Transition name="filter-drawer">
        <div v-if="showFilterDialog" class="filter-drawer-backdrop" @mousedown.self="showFilterDialog = false">
            <div class="filter-drawer">
            <div class="filter-drawer__header">
              <div>
                <h2 class="mt-0 mb-0">Filters</h2>
                <p class="filter-drawer__count">{{ filteredImageCount }} of {{ imageCount }} images</p>
              </div>
              <div class="filter-drawer__header-actions">
                <button v-if="activeFilterCount > 0" class="clear-filters-btn" @click="clearAllFilters">
                  Clear All
                </button>
                <IconButton
                  :icon="'close'"
                  :width="28"
                  :height="28"
                  @click="showFilterDialog = false"
                />
              </div>
            </div>
            <div class="filter-drawer__content scrollbar">
              <DatasetFilter
                v-model="datasetFilters"
                :dataset="currentDataset"
                :annotationSets="annotationSets"
                :labels="currentLabels"
                :types="currentTypes"
                :collapsible="false"
              />

              <div style="margin-top: 16px;width: 100%;">
              <GalleryImageSort v-model:sortBy="sortBy" v-model:ascend="reverse"  />
              </div>

            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Confirmation Modal -->
    <ConfirmModal
      :show="showConfirmModal"
      ref="confirmModal"
      :messageHeader="confirmMessageHeader"
      :message="confirmMessage"
      :buttonClass="buttonClass"
      :buttonText="buttonText"
      @confirmed="handleConfirmAction"
    />

    <!-- Export Modal for Sequences -->
    <DatasetExportModal
      ref="datasetExportModal"
      :exportDataset="currentDataset"
      :sequences="sequences"
    />

    <!-- Copy Modal -->
    <GalleryCopy
      ref="galleryCopyModal"
      :source="currentDataset"
      :datasets="availableDatasets"
      :selectedImages="selectedImages"
      :mode="activeTab"
      @copy-in-progress="handleCopyCompleted"
      @closed="showCopyModal = false"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

import DisplayAnnotationsFilter from '@/components/DatasetComponent/GalleryComponent/DisplayAnnotationsFilterV2.vue';
import GalleryImageSort from '@/components/DatasetComponent/GalleryComponent/GalleryImageSort.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import GalleryGrid from '@/components/DatasetComponent/GalleryComponent/GalleryGrid.vue';
import GalleryHeader from '@/components/DatasetComponent/GalleryComponent/GalleryHeader.vue';
import UploadImagesModal from '@/components/DatasetComponent/UploadImagesModal.vue';
import ImageCard from '@/components/DatasetComponent/GalleryComponent/ImageCard.vue';
import SequenceCard from '@/components/DatasetComponent/GalleryComponent/SequenceCard.vue';
import GalleryEmptyState from '@/components/DatasetComponent/GalleryComponent/GalleryEmptyState.vue';
import GalleryDropZone from '@/components/DatasetComponent/GalleryComponent/GalleryDropZone.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import Header from '@/components/Header.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import GalleryCopy from '@/components/DatasetComponent/GalleryComponent/GalleryCopy.vue';
import DatasetExportModal from '@/components/DatasetComponent/DatasetManagement/DatasetExportModal.vue';
import DisplayImageDetails from '@/components/DatasetComponent/GalleryComponent/DisplayImageDetails.vue';
import useGallery from '@/composables/annotationTool/useGallery.js';
import DatasetFilter from '@/components/DatasetComponent/DatasetFilters/DatasetFilter.vue';
import GalleryActionsDropdown from '@/components/DatasetComponent/GalleryComponent/GalleryActionsDropdown.vue';
import PageControl from '@/components/DatasetComponent/GalleryComponent/PageControl.vue';
import FilterPopper from '@/components/FilterPopper.vue';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue';
import GallerySeqList from '@/components/DatasetComponent/GalleryComponent/GallerySeqList.vue';
import GalleryImageList from '@/components/DatasetComponent/GalleryComponent/GalleryImageList.vue';
import equal from 'deep-equal';
import useTaskModal from '@/composables/useTaskModal.js';
import { encodeID } from '@/assets/js/utils.js';

// Props
const props = defineProps({
  projectId: {
    type: [String, Number],
    default: null
  }
});

// Composables
const route = useRoute();
const router = useRouter();
const store = useStore();
const toast = useToast();

const {
  datasetFilters,
  annotationDisplaySettings,
  imageDisplaySettings,
  reverse,
  combineSequenceFrames,
  sortBy,
  internalCurrentDataset,
  currentPage,
  imagesPerPage,
  imageList,
  getImagesParams,
  getImagesFilterParams,
  getImages,
  deleteFilteredImages,
  isFetchingImages,
} = useGallery();

const {
  pauseGetTasks,
  resumeGetTasks,
  isGettingTasks,
  getTasks,
} = useTaskModal();

combineSequenceFrames.value = false;

// Data properties
const dataConnect = new DatastoreConnect(store.state.enterpriseServerUrl);
const dsID = ref(null);
const imageListPromise = ref(null);
const mapImageList = ref([]);
const totalPagesCount = ref(0);
const dropHighlighted = ref(false);
const gallerySelection = ref([]);
const confirmMessage = ref(null);
const confirmMessageHeader = ref(null);
const buttonClass = ref('button-delete');
const buttonText = ref('');
const confirmFunction = ref(null);
const showConfirmModal = ref(false);
const showCopyModal = ref(false);
const showCopyImageModal = ref(false);
const showFilterDialog = ref(false);
const permissions = ref(null);
const selectedImage = ref(null);
const sequences = ref([]);
const datasetExportModal = ref(null);
const showExportModal = ref(false);
const galleryCopyModal = ref(null);
const doneFetch = ref(true);
const selectedImages = ref([]);
const selectAllPageFiles = ref(false);
const selectAllFiles = ref(false);
const hasSequences = ref(false);
const activeTab = ref('images');
const seqCount = ref(0);
const imageCount = ref(0);
const filteredImageCount = ref(0);
const seqList = ref([]);

const isFetchingSequences = ref(false);
const datasets = ref([]);
const currentDataset = ref(null);
const currentProject = ref(null);
const isInitializing = ref(true);
const isLoadingInitial = ref(true);
const currentLabels = ref([]);
const currentTypes = ref([]);
const annotationSets = ref([]);


// ========================================= LIFE CYCLE METHODS =========================================
onMounted(async () => {
  // Always reset to page 1 on mount
  currentPage.value = 1;
  
  // Clear all filters on mount
  clearAllFilters();
  
  const HexDsID = route.params.datasetId || route.query.dataset_id || route.query.dataset;
  if (!HexDsID) {
    toast.error('No dataset specified. Please select a dataset to view its gallery.', {
      position: 'top-right',
      duration: 5000,
    });
  }

  dsID.value = route.params.datasetId ? parseInt(HexDsID, 16) : parseInt(HexDsID);
  await getDatasetDetail(dsID.value);
 
  //restore url params if provided
  if (route.query.image_params) {
    const image_params = JSON.parse(decodeURI(route.query.image_params));
    imagesPerPage.value = image_params.imagesPerPage;
    currentPage.value = image_params.currentPage;
    sortBy.value = image_params.sortBy;
    reverse.value = image_params.reverse;
  }

  if (route.query.filter_params) {
    datasetFilters.value = JSON.parse(decodeURI(route.query.filter_params));
  }

  if (route.query.annotation_display) {
    annotationDisplaySettings.value = JSON.parse(decodeURI(route.query.annotation_display));
  }

  if (route.query.image_display) {
    imageDisplaySettings.value = JSON.parse(decodeURI(route.query.image_display));
  }

  await GetPermissionsForCurrentUser();
  await getSequences(dsID.value);

  isLoadingInitial.value = false;
  isInitializing.value = false;

  if (seqCount.value === 0) {
    activeTab.value = 'images';
  } else {
    activeTab.value = 'sequences';
  }


  
});

//==================================================================================================
const getSequences = async (dsID) => {
  if (!dsID) return;
  const resp = await dataConnect.getSequences({
    dataset_id: dsID,
  }).catch(() => null);
  seqList.value = resp?.result || [];
  if (seqList.value.length > 0) {
    hasSequences.value = true;
    seqCount.value = seqList.value.length;
  } else {
    hasSequences.value = false;
    seqCount.value = 0;
  }
};


//------------------------------------------------------------------------------
const getDatasetDetail = async (dataset_id) => {
  if (dataset_id) {
    const params = {
      dataset_id,
      get_num_images: true,
      get_labels: true,
      get_num_labels: true,
      get_annotation_sets: true,
      get_groups: true,
      get_docker_tasks: true,
    };
    await dataConnect.getDatasetById(params)
      .then((resp) => {
        if (!resp.error) {
          const temp = resp.result;
          currentDataset.value = temp;
          currentLabels.value = temp.labels || [];
          currentTypes.value = temp.annotation_types || [];
          annotationSets.value = temp.annotation_sets || [];
          imageCount.value = temp.numImages || 0;
          filteredImageCount.value = temp.numImages || 0;
        } else {
          let msg = resp.error?.message || String(resp.error);
          if (msg.includes('no rows')) {
            msg = 'Dataset does not exist.';
          }
          toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to retrieve dataset: ${msg}`,
            type: 'error',
            position: 'top',
          });
        }
      })
      .catch((error) => {
        let msg = error?.message || String(error);
        if (msg.includes('no rows')) {
          msg = 'Dataset does not exist.';
        }
        toast.open({
          queue: true,
          duration: 5000,
          message: `Failed to retrieve dataset: ${msg}`,
          type: 'error',
          position: 'top',
        });
      });
  }
};


// Fetch images with explicit offset, count, and filters
const fetchImages = async (offset, count, filters = {}) => {
  try {
    const params = {
      get_annotations: true,
      get_sensors: true,
      get_tags: true,
      combine_sequence_frames: combineSequenceFrames.value,
      sort_by: sortBy.value,
      reverse: reverse.value,
      offset: offset,
      limit: count,
      images_filter: { 
        dataset_id: dsID.value,
        ...filters.images_filter,
      },
      image_files_filter: filters.image_files_filter || {},
      annotations_filter: filters.annotations_filter || null,
      image_review_status_filter: filters.image_review_status_filter || null,
      image_tags_filter: filters.image_tags_filter || null,
      sensors_filter: filters.sensors_filter || null,
    };
    
    const resp = await dataConnect.getFilteredImages(params);
    return resp?.result || [];
  } catch (error) {
    console.error('Error in fetchImages:', error);
    throw error;
  }
};


const seqSelectedHandler = async () => {
  

  
};



const imageSelectedHandler = async () => {
  doneFetch.value = false;
  try {
    const offset = (currentPage.value - 1) * imagesPerPage.value;
    // console.log('Fetching images for page:', currentPage.value, 'offset:', offset, 'count:', imagesPerPage.value);
    
    // Build filters from datasetFilters
    const filters = {
      images_filter: {},
      image_files_filter: {},
      annotations_filter: null,
      image_review_status_filter: null,
      image_tags_filter: null,
      sensors_filter: null,
    };

    // Images filter
    if (datasetFilters.value?.groupsFilter && datasetFilters.value.groupsFilter.length > 0) {
      filters.images_filter.group_ids = datasetFilters.value.groupsFilter;
    }

    // Image files filter (includes name filter)
    if (datasetFilters.value?.imageNameFilter?.length > 0) {
      filters.image_files_filter.image_name = datasetFilters.value.imageNameFilter;
    }
    if (datasetFilters.value?.timeRangeFilter) {
      filters.image_files_filter.lower_time = datasetFilters.value.timeRangeFilter[0];
      filters.image_files_filter.upper_time = datasetFilters.value.timeRangeFilter[1];
    }
    if (datasetFilters.value?.dateRangeFilter) {
      if (datasetFilters.value.dateRangeFilter[0]) {
        filters.image_files_filter.lower_date = new Date(datasetFilters.value.dateRangeFilter[0]).toISOString();
      }
      if (datasetFilters.value.dateRangeFilter[1]) {
        filters.image_files_filter.upper_date = new Date(datasetFilters.value.dateRangeFilter[1]).toISOString();
      }
    }
    if (datasetFilters.value?.cameraSourceFilter && datasetFilters.value.cameraSourceFilter.length > 0) {
      filters.image_files_filter.image_sources = datasetFilters.value.cameraSourceFilter;
    }

    // Annotations filter
    const annotationsFilter = {};
    if (datasetFilters.value?.confidenceRangeFilter) {
      annotationsFilter.lower_score = datasetFilters.value.confidenceRangeFilter[0];
      annotationsFilter.upper_score = datasetFilters.value.confidenceRangeFilter[1];
    }
    if (datasetFilters.value?.annotationTypeFilter?.length > 0) {
      annotationsFilter.types = datasetFilters.value.annotationTypeFilter;
      if (datasetFilters.value.annotationTypeFilterInvert) {
        annotationsFilter.types_inverse = true;
      }
    }
    if (datasetFilters.value?.annotationSetFilter?.length > 0) {
      annotationsFilter.annotation_set_ids = datasetFilters.value.annotationSetFilter.map((set) => set.id);
    }
    if (datasetFilters.value?.imagesWithAnnotationSetFilter?.length > 0) {
      annotationsFilter.images_with_annotation_set_ids = datasetFilters.value.imagesWithAnnotationSetFilter.map((set) => set.id);
    }
    if (datasetFilters.value?.annotationLabelFilter?.length > 0) {
      annotationsFilter.images_with_annotation_label_indexes = datasetFilters.value.annotationLabelFilter;
    }
    if (datasetFilters.value?.boundingBoxSizeFilter) {
      Object.entries(datasetFilters.value.boundingBoxSizeFilter).forEach(([key, value]) => {
        if (key !== 'type' && value) {
          annotationsFilter[`${key}_${datasetFilters.value.boundingBoxSizeFilter.type}`] = value;
        }
      });
    }
    filters.annotations_filter = Object.keys(annotationsFilter).length ? annotationsFilter : null;

    // Tags filter
    const imageTagFilter = {};
    if (datasetFilters.value?.imageTagFilter && datasetFilters.value.imageTagFilter.length > 0) {
      imageTagFilter.tag_names = datasetFilters.value.imageTagFilter.map((tag) => tag.name);
    }
    filters.image_tags_filter = Object.keys(imageTagFilter).length ? imageTagFilter : null;

    // console.log('Applying filters:', filters);
    const imagesResult = await fetchImages(offset, imagesPerPage.value, filters);
    // console.log('Fetched', imagesResult?.length || 0, 'images');
    imageList.value = imagesResult || [];
  } catch (error) {
    console.error('Error fetching images:', error);
    toast.open({
      queue: true,
      duration: 5000,
      message: `Failed to load images: ${error.message}`,
      type: 'error',
      position: 'top',
    });
  } finally {
    doneFetch.value = true;
  }
};

const getFilteredImageCount = async () => {
  try {
    // Build filters from datasetFilters (same as imageSelectedHandler)
    const filters = {
      images_filter: {},
      image_files_filter: {},
      annotations_filter: null,
      image_review_status_filter: null,
      image_tags_filter: null,
      sensors_filter: null,
    };

    // Images filter
    if (datasetFilters.value?.groupsFilter && datasetFilters.value.groupsFilter.length > 0) {
      filters.images_filter.group_ids = datasetFilters.value.groupsFilter;
    }

    // Image files filter (includes name filter)
    if (datasetFilters.value?.imageNameFilter?.length > 0) {
      filters.image_files_filter.image_name = datasetFilters.value.imageNameFilter;
    }
    if (datasetFilters.value?.timeRangeFilter) {
      filters.image_files_filter.lower_time = datasetFilters.value.timeRangeFilter[0];
      filters.image_files_filter.upper_time = datasetFilters.value.timeRangeFilter[1];
    }
    if (datasetFilters.value?.dateRangeFilter) {
      if (datasetFilters.value.dateRangeFilter[0]) {
        filters.image_files_filter.lower_date = new Date(datasetFilters.value.dateRangeFilter[0]).toISOString();
      }
      if (datasetFilters.value.dateRangeFilter[1]) {
        filters.image_files_filter.upper_date = new Date(datasetFilters.value.dateRangeFilter[1]).toISOString();
      }
    }
    if (datasetFilters.value?.cameraSourceFilter && datasetFilters.value.cameraSourceFilter.length > 0) {
      filters.image_files_filter.image_sources = datasetFilters.value.cameraSourceFilter;
    }

    // Annotations filter
    const annotationsFilter = {};
    if (datasetFilters.value?.confidenceRangeFilter) {
      annotationsFilter.lower_score = datasetFilters.value.confidenceRangeFilter[0];
      annotationsFilter.upper_score = datasetFilters.value.confidenceRangeFilter[1];
    }
    if (datasetFilters.value?.annotationTypeFilter?.length > 0) {
      annotationsFilter.types = datasetFilters.value.annotationTypeFilter;
      if (datasetFilters.value.annotationTypeFilterInvert) {
        annotationsFilter.types_inverse = true;
      }
    }
    if (datasetFilters.value?.annotationSetFilter?.length > 0) {
      annotationsFilter.annotation_set_ids = datasetFilters.value.annotationSetFilter.map((set) => set.id);
    }
    if (datasetFilters.value?.imagesWithAnnotationSetFilter?.length > 0) {
      annotationsFilter.images_with_annotation_set_ids = datasetFilters.value.imagesWithAnnotationSetFilter.map((set) => set.id);
    }
    if (datasetFilters.value?.annotationLabelFilter?.length > 0) {
      annotationsFilter.images_with_annotation_label_indexes = datasetFilters.value.annotationLabelFilter;
    }
    if (datasetFilters.value?.boundingBoxSizeFilter) {
      Object.entries(datasetFilters.value.boundingBoxSizeFilter).forEach(([key, value]) => {
        if (key !== 'type' && value) {
          annotationsFilter[`${key}_${datasetFilters.value.boundingBoxSizeFilter.type}`] = value;
        }
      });
    }
    filters.annotations_filter = Object.keys(annotationsFilter).length ? annotationsFilter : null;

    // Tags filter
    const imageTagFilter = {};
    if (datasetFilters.value?.imageTagFilter && datasetFilters.value.imageTagFilter.length > 0) {
      imageTagFilter.tag_names = datasetFilters.value.imageTagFilter.map((tag) => tag.name);
    }
    filters.image_tags_filter = Object.keys(imageTagFilter).length ? imageTagFilter : null;

    // Call API with only_count: true to get filtered count
    const params = {
      only_count: true,
      combine_sequence_frames: combineSequenceFrames.value,
      images_filter: { 
        dataset_id: dsID.value,
        ...filters.images_filter,
      },
      image_files_filter: filters.image_files_filter || {},
      annotations_filter: filters.annotations_filter || null,
      image_review_status_filter: filters.image_review_status_filter || null,
      image_tags_filter: filters.image_tags_filter || null,
      sensors_filter: filters.sensors_filter || null,
    };

    const resp = await dataConnect.getFilteredImages(params);
    filteredImageCount.value = resp?.result || 0;
    // console.log('Filtered image count:', filteredImageCount.value);
  } catch (error) {
    console.error('Error fetching filtered image count:', error);
    filteredImageCount.value = 0;
  }
};

const isEmpty = ref(false);

// Computed properties
const canWrite = computed(() => {
  return permissions.value && permissions.value.dataset_write;
});

const currentDatasetID = computed(() => {
  return currentDataset.value?.id || 0;
});

const availableDatasets = computed(() => {
  const allDatasets = store.state.datasets.datasetList || [];
  const currentProjectId = currentProject.value?.id;
  const currentDatasetId = currentDataset.value?.id;

  if (!currentProjectId || !currentDatasetId) {
    return [];
  }

  return allDatasets.filter(
    ds => ds.project_id === currentProjectId && ds.id !== currentDatasetId,
  );
});

const displayedImageList = computed(() => {
  if (activeTab.value === 'sequences') {
    return seqList.value;
  }
  return imageList.value;
});

const numPages = computed(() => {
  if (activeTab.value === 'sequences') {
    return Math.ceil(seqCount.value / imagesPerPage.value);
  }
  return Math.ceil(imageCount.value / imagesPerPage.value);
});


const getAnnotationSetsParams = computed(() => {
  if (!currentDataset.value) {
    return null;
  }

  const params = {
    dataset_id: currentDataset.value.id,
  };

  if (route.name === 'annotations') {
    params.include_empty = true;
  }

  return params;
});

const gridItemDisplayProp = computed(() => {
  if (!datasetFilters.value?.displayAnnotationsFilter) {
    return {};
  }
  if (datasetFilters.value?.displayAnnotationsFilter?.displayType === 'byAnnotationSets') {
    return { filterAnnotationsBySets: datasetFilters.value.displayAnnotationsFilter.value };
  }
  if (datasetFilters.value?.displayAnnotationsFilter?.displayType === 'byAnnotationLabels') {
    return { filterAnnotationsByLabelIndexes: datasetFilters.value.displayAnnotationsFilter.value };
  }
  return {};
});

const colorAnnotationsBy = computed(() => {
  if (annotationDisplaySettings.value?.colorAnnotationsBy?.type) {
    return annotationDisplaySettings.value?.colorAnnotationsBy?.type;
  }
  return 'set';
});

const displayAnnotationSets = computed(() => {
  if (annotationDisplaySettings.value?.displayAnnotationSets) {
    return annotationDisplaySettings.value?.displayAnnotationSets.annotation_sets;
  }
  return [];
});

const displayLabels = computed(() => {
  if (annotationDisplaySettings.value?.displayLabels) {
    return annotationDisplaySettings.value?.displayLabels.labels;
  }
  return [];
});

const showImageName = computed(() => {
  if (imageDisplaySettings.value?.includes('showName')) {
    return true;
  }
  return false;
});

const showSequences = computed(() => {
  if (imageDisplaySettings.value?.includes('showSequences')) {
    return true;
  }
  return false;
});

const showLabels = computed(() => {
  if (imageDisplaySettings.value?.includes('showLabels')) {
    return true;
  }
  return false;
});

const imageParams = computed(() => {
  return getImagesParams();
});

const imageFilterParams = computed(() => {
  return getImagesFilterParams();
});

const getImagesCombinedParams = computed(() => {
  return { ...getImagesParams(), ...getImagesFilterParams() };
});

const combinedParams = computed(() => {
  return {
    ...annotationDisplaySettings.value,
    ...imageDisplaySettings.value,
  };
});

const minImageNumber = computed(() => {
  return ((currentPage.value - 1) * imagesPerPage.value) + 1;
});

const maxImageNumber = computed(() => {
  const imgListLength = imageList.value ? imageList.value.length : 0;
  return ((currentPage.value - 1) * imagesPerPage.value) + imgListLength;
});

const isPublic = computed(() => {
  return route.path.includes('/public') || currentProject.value?.is_public || false;
});

const activeFilterCount = computed(() => {
  let count = 0;
  const filters = datasetFilters.value;
  
  // Count non-empty filters
  if (filters?.groupsFilter?.length > 0) count++;
  if (filters?.imageNameFilter?.length > 0) count++;
  if (filters?.timeRangeFilter) count++;
  if (filters?.dateRangeFilter) count++;
  if (filters?.cameraSourceFilter?.length > 0) count++;
  if (filters?.confidenceRangeFilter) count++;
  if (filters?.annotationTypeFilter?.length > 0) count++;
  if (filters?.annotationSetFilter?.length > 0) count++;
  if (filters?.imagesWithAnnotationSetFilter?.length > 0) count++;
  if (filters?.annotationLabelFilter?.length > 0) count++;
  if (filters?.boundingBoxSizeFilter?.lower_height || filters?.boundingBoxSizeFilter?.lower_width || filters?.boundingBoxSizeFilter?.upper_height || filters?.boundingBoxSizeFilter?.upper_width) count++;
  if (filters?.imageTagFilter?.length > 0) count++;
  
  return count;
});

// Methods


//MUID


// const fetchSequences = async () => {
//   if (!currentDatasetID.value) return;
//   isFetchingSequences.value = true;
//   const resp = await dataConnect.getSequences({
//     dataset_id: currentDatasetID.value,
//   }).catch((e) => { console.error('[fetchSequences] getSequences error:', e); return null; });
//   const seqList = resp?.result || [];
//   seqCount.value = seqList.length;
//   if (seqList.length === 0) {
//     sequenceList.value = [];
//     isFetchingSequences.value = false;
//     return;
//   }



//   const imgResp = await dataConnect.getFilteredImages({
//     combine_sequence_frames: true,
//     images_filter: { dataset_id: currentDatasetID.value },
//     image_files_filter: {},
//     annotations_filter: null,
//     limit: 1000,
//     offset: 0,
//   }).catch((e) => { console.error('[fetchSequences] getFilteredImages error:', e); return null; });
//   if (imgResp?.error) {
//     console.error('[fetchSequences] API error:', imgResp.error);
//   }
//   const imgList = Array.isArray(imgResp?.result) ? imgResp.result : [];
//   const seqImageMap = {};
//   imgList.forEach((img) => {
//     if (img.sequence_id && !seqImageMap[img.sequence_id]) {
//       seqImageMap[img.sequence_id] = img.id;
//     }
//   });
//   sequenceList.value = seqList.map((seq) => ({
//     ...seq,
//     _firstImageId: seqImageMap[seq.id] || null,
//   }));
//   isFetchingSequences.value = false;
// };

const getAnnotationSetsForDataset = async (dataset) => {
  if (dataset) {
    const resp = await dataConnect.getAnnotationSets({
      dataset_id: dataset.id,
      get_num_annotations: true,
      get_num_images: true,
      get_labels: true,
    })
      .catch((error) => {
        console.log('Failed to retrieve sets:', error);
      });
    if (resp && !resp.error && resp.result) {
      annotationSets.value = resp.result;
    }
  }
};

const GetPermissionsForCurrentUser = async () => {
  const user = store.state?.user?.user;
  let username = '';
  if (user) {
    username = user.username;
  }
  if (currentDataset.value) {
    await dataConnect.getProjectAccessForSingleUser({
      project_id: currentDataset.value.project_id,
      username: username,
    })
      .then((data) => {
        if (data.result) {
          permissions.value = data.result;
        }
      })
      .catch((e) => console.log(e));
  } else {
    permissions.value = null;
  }
};

const getAnnotationSetList = async (params) => {
  const annotationSets = await dataConnect.getAnnotationSets(params)
    .catch((error) => {
      console.log(error);
    });
  if (annotationSets.error || !annotationSets.result) {
    return {};
  }
  return annotationSets.result;
};

const handleGallerySelectionChanged = (selection) => {
  gallerySelection.value = selection;
};

const handleCopyCompleted = async () => {
  // Datasets are loaded directly from API, no need to refresh from store
};

const handleAnnotationSetCreated = async () => {
  annotationSets.value = await getAnnotationSetList();
};

const openConfirmDeleteFilteredImagesModal = () => {
  confirmMessage.value = 'Are you sure you want to delete all filtered images';
  confirmMessageHeader.value = 'Delete Filtered Images';
  buttonClass.value = 'button-delete';
  buttonText.value = 'Delete';
  confirmFunction.value = () => deleteFilteredImages();
};

const openConfirmDeleteImageModal = () => {
  const filtered = selectedImages.value.reduce((acc, item) => {
    if (item.sequence_id && item.sequence_id > 0 && item.type === 'sequence') {
      acc.sequence_ids.push(item.sequence_id);
    } else if (item.id > 0 && item.type === 'image') {
      acc.image_ids.push(item.id);
    } else {
      acc.image_ids.push(item.id);
    }
    return acc;
  }, { sequence_ids: [], image_ids: [] });
  let numImagesToDelete = 0;
  if (selectAllFiles.value) {
    numImagesToDelete = imageCount.value;
  } else {
    numImagesToDelete = selectedImages.value.length;
  }
  confirmMessage.value = `Are you sure you want to permanently delete ${numImagesToDelete} item(s)?`;
  confirmMessageHeader.value = 'Deleting Items';
  buttonClass.value = 'button-delete';
  buttonText.value = 'Delete';
  confirmFunction.value = async () => {
    await deleteSelectedImagesAndSeq(filtered.image_ids, filtered.sequence_ids);
    await getSequences(dsID.value);
  };
  showConfirmModal.value = true;
};

const handleDeleteSingle = (obj) => {
  const isSeqFromList = obj.no_of_images !== undefined;
  const isSeqFromImages = obj.sequence_id && obj.sequence_id > 0 && obj.type === 'sequence';
  const isSeq = isSeqFromList || isSeqFromImages;
  confirmMessage.value = `Are you sure you want to permanently delete this ${isSeq ? 'sequence' : 'image'}?`;
  confirmMessageHeader.value = `Deleting ${isSeq ? 'Sequence' : 'Image'}`;
  buttonClass.value = 'button-delete';
  buttonText.value = 'Delete';
  const seqIds = isSeq ? [isSeqFromList ? obj.id : obj.sequence_id] : [];
  const imgIds = isSeq ? [] : [obj.id];
  confirmFunction.value = () => {
    deleteSelectedImagesAndSeq(imgIds, seqIds).then(() => {
      if (isSeq && activeTab.value === 'sequences') {
        fetchSequences();
      }
    });
  };
};


const deleteSelectedImagesAndSeq = async (image_ids, sequence_ids) => {
  if (image_ids.length === 0 && sequence_ids.length === 0) {
    return;
  }

    const params = {
      dataset_id: currentDatasetID.value,
      images_filter: { 
        dataset_id: currentDatasetID.value,
        image_ids: image_ids,
        sequence_ids: sequence_ids, 
       },
      image_files_filter: {},
      annotations_filter: null,
      image_review_status_filter: null,
      image_tags_filter: null,
      sensors_filter: null,
    };

  return dataConnect.deleteFilteredImages2(params)
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error.message);
      }
      // Reset to page 1 and refresh dataset details and images
      currentPage.value = 1;
      getDatasetDetail(dsID.value);
      return getImages().then((imageListResult) => {
        imageList.value = imageListResult;
      });
    })
    .catch((error) => {
      const toastInstance = useToast();
      toastInstance.open({
        queue: true,
        duration: 5000,
        message: error.message,
        type: 'error',
        position: 'top',
      });
      throw error;
    })
    .finally(() => {
      selectedImages.value = [];
      selectAllPageFiles.value = false;
      selectAllFiles.value = false;
    });
};




const deleteFilteredImages2 = async (image_ids, sequence_ids) => {
  if (image_ids.length === 0 && sequence_ids.length === 0 && !selectAllFiles.value) {
    return;
  }

  const params = {
    ...imageFilterParams.value,
    dataset_id: currentDatasetID.value,
  };

  if (!selectAllFiles.value) {
    if (imageFilterParams.value.images_filter && image_ids.length > 0) {
      imageFilterParams.value.images_filter.image_ids = image_ids;
    }
    if (imageFilterParams.value.images_filter && sequence_ids.length > 0) {
      imageFilterParams.value.images_filter.sequence_ids = sequence_ids;
    }
  }

  return dataConnect.deleteFilteredImages2(params)
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error.message);
      }
      return getImages().then((imageListResult) => {
        imageList.value = imageListResult;
      });
    })
    .catch((error) => {
      const toastInstance = useToast();
      toastInstance.open({
        queue: true,
        duration: 5000,
        message: error.message,
        type: 'error',
        position: 'top',
      });
      throw error;
    })
    .finally(() => {
      selectedImages.value = [];
      selectAllPageFiles.value = false;
      selectAllFiles.value = false;
    });
};

const openConfirmDeleteSequencesModal = () => {
  const seqIds = selectedImages.value
    .filter((item) => item.id && item.no_of_images !== undefined)
    .map((item) => item.id);
  
  if (seqIds.length === 0) {
    return;
  }

  confirmMessage.value = `Are you sure you want to permanently delete ${seqIds.length} sequence(s)?`;
  confirmMessageHeader.value = 'Deleting Sequences';
  buttonClass.value = 'button-delete';
  buttonText.value = 'Delete';
  confirmFunction.value = async () => {
    await deleteSelectedImagesAndSeq([], seqIds);
    selectedImages.value = [];
    selectAllPageFiles.value = false;
    selectAllFiles.value = false;
    await getSequences(dsID.value);
  };
  showConfirmModal.value = true;
};

const deleteImageFromDataset = async (sequence_ids, image_ids) => {
  const params = {
    dataset_id: currentDataset.value.id,
    sequence_ids,
    image_ids,
  };
  dataConnect.deleteImageFromDataset(params)
    .then((data) => {
      if (!data || data.error || !data.result) {
        const toastInstance = useToast();
        toastInstance.open({
          queue: true,
          duration: 5000,
          message: data.error.message,
          type: 'error',
          position: 'top',
        });
      } else {
        getImages().then((imageListResult) => {
          imageList.value = imageListResult;
        });
      }
    });
  selectedImages.value = [];
  selectAllPageFiles.value = false;
  selectAllFiles.value = false;
};

const displayImage = (imageObj) => {
  const isCurrentRoutePublic = route.name.includes('public');
  const hasNewFormat = !!route.params.datasetId;
  
  if (hasNewFormat) {
    router.push({
      name: `${isCurrentRoutePublic ? 'public.' : ''}datasets.gallery.new.viewer`,
      params: { datasetId: route.params.datasetId },
      query: {
        image_id: encodeURI(JSON.stringify(imageObj.id)),
        annotationDisplaySettings: encodeURI(JSON.stringify(annotationDisplaySettings.value)),
        showSequences: encodeURI(imageDisplaySettings.value.includes('showSequences')),
      },
    });
  } else {
    const projectId = currentProject.value ? encodeID(currentProject.value.id, 'p') : '';
    router.push({
      name: `${isCurrentRoutePublic ? 'public.' : ''}datasets.gallery.viewer`,
      params: { projectId: isCurrentRoutePublic ? '' : projectId },
      query: {
        dataset: currentDatasetID.value,
        image_id: encodeURI(JSON.stringify(imageObj.id)),
        annotationDisplaySettings: encodeURI(JSON.stringify(annotationDisplaySettings.value)),
        showSequences: encodeURI(imageDisplaySettings.value.includes('showSequences')),
      },
    });
  }
};

const displaySequence = (seq) => {
  if (seq._firstImageId) {
    const isCurrentRoutePublic = route.name.includes('public');
    const hasNewFormat = !!route.params.datasetId;
    
    if (hasNewFormat) {
      router.push({
        name: `${isCurrentRoutePublic ? 'public.' : ''}datasets.gallery.new.viewer`,
        params: { datasetId: route.params.datasetId },
        query: {
          image_id: encodeURI(JSON.stringify(seq._firstImageId)),
          annotationDisplaySettings: encodeURI(JSON.stringify(annotationDisplaySettings.value)),
          showSequences: 'true',
        },
      });
    } else {
      const projectId = currentProject.value ? encodeID(currentProject.value.id, 'p') : '';
      router.push({
        name: `${isCurrentRoutePublic ? 'public.' : ''}datasets.gallery.viewer`,
        params: { projectId: isCurrentRoutePublic ? '' : projectId },
        query: {
          dataset: currentDatasetID.value,
          image_id: encodeURI(JSON.stringify(seq._firstImageId)),
          annotationDisplaySettings: encodeURI(JSON.stringify(annotationDisplaySettings.value)),
          showSequences: 'true',
        },
      });
    }
  }
};

const toggleSequenceSelection = (seq, isSelected) => {
  if (isSelected) {
    if (!selectedImages.value.some((s) => s.id === seq.id)) {
      selectedImages.value = [...selectedImages.value, seq];
    }
  } else {
    selectedImages.value = selectedImages.value.filter((s) => s.id !== seq.id);
  }
  selectAllPageFiles.value = false;
  selectAllFiles.value = false;
};

const openUploadModal = () => {
  // This will be accessed via template ref
};

const handleImageUploadComplete = () => {
  imageListPromise.value = new Promise((resolve, reject) => {
    getImages().then((imageListResult) => {
      imageList.value = imageListResult;
      resolve(imageListResult);
    });
  });
};

const handleUploadError = async (data) => {
  confirmMessage.value = data.confirmMessage;
  confirmMessageHeader.value = data.confirmMessageHeader;
  buttonText.value = '';
};

const openCopyFilteredImagesModal = () => {
  showCopyModal.value = true;
};

const confirmCopy = async (args) => {
  confirmMessage.value = `Destination dataset contains ${args.duplicate_count} ${args.duplicate_count === 1 ? 'image' : 'images'} with the same name. 
      ${args.duplicate_count === 1 ? 'This image' : 'These images'} will be ignored.`;
  confirmMessageHeader.value = 'Duplicate files';
  buttonClass.value = '';
  buttonText.value = 'Confirm';
  confirmFunction.value = async () => { await handleConfirmCopy(args.fullParams); };
};

const handleConfirmCopy = async (params) => {
  await dataConnect.confirmCopySequences(params)
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error.message);
      } else if (resp.result) {
        const destDS = datasets.value.find((e) => e.id === params.dest_dataset_id);
        if (!destDS) {
          toast.open({
            queue: true,
            duration: 5000,
            message: 'Copying in progress. Refresh the page to view progress.',
            type: 'success',
            position: 'top',
          });
        } else {
          toast.open({
            queue: true,
            duration: 5000,
            message: 'Copying in progress.',
            type: 'success',
            position: 'top',
          });
        }

        getTasks();
        if (!isGettingTasks.value) {
          resumeGetTasks();
        }

        showCopyModal.value = false;
      }
    })
    .catch((error) => {
      toast.open({
        queue: true,
        duration: 5000,
        message: `Failed to copy: ${error.message}`,
        type: 'error',
        position: 'top',
      });
    });
};

const handleCopy = (imageObj) => {
  if (!selectedImages.value.includes(imageObj)) {
    selectedImages.value.push(imageObj);
  }
  showCopyModal.value = true;
};

const handleDownload = (obj) => {
  if (obj.type === 'sequence') {
    sequences.value = [{ id: obj.sequence_id, uuid: obj.uuid, name: obj.name }];
  } else if (obj.no_of_images !== undefined) {
    sequences.value = [{ id: obj.id, uuid: obj.uuid, name: obj.name }];
  }
};

const handleSelectAllPageFiles = () => {
  if (!selectAllPageFiles.value) {
    selectedImages.value = imageList.value;
    selectAllPageFiles.value = true;
    selectAllFiles.value = false;
  } else {
    selectedImages.value = [];
    selectAllPageFiles.value = false;
    selectAllFiles.value = false;
  }
};

const handleSelectAllFiles = () => {
  selectAllFiles.value = true;
  selectedImages.value = [];
};

const handleClearSelectAllFiles = () => {
  selectedImages.value = [];
  selectAllPageFiles.value = false;
  selectAllFiles.value = false;
};

const toggleImageSelection = (imageObj, isSelected) => {
  if (isSelected) {
    if (!selectedImages.value.some((img) => img.id === imageObj.id)) {
      selectedImages.value = [...selectedImages.value, imageObj];
    }
  } else {
    selectedImages.value = selectedImages.value.filter(
      (img) => img.id !== imageObj.id,
    );
  }
  selectAllPageFiles.value = false;
  selectAllFiles.value = false;
};

const handleMultiSelect = (IDs) => {
  selectedImages.value = imageList.value.filter((e) => IDs.includes(e.id));
};

const handleRemoveTagsForSelectedImages = (tag_name) => {
  const selectedImageIds = selectedImages.value.map((e) => e.id);
  imageList.value.forEach((image, index) => {
    if (selectedImageIds.includes(image.id)) {
      imageList.value[index].tags = imageList.value[index].tags.filter((e) => e.name !== tag_name);
    }
  });
};

const handleAddTagsForSelectedImages = (tag_name) => {
  const selectedImageIds = selectedImages.value.map((e) => e.id);
  imageList.value.forEach((image, index) => {
    if (selectedImageIds.includes(image.id)) {
      imageList.value[index].tags.push({ name: tag_name });
    }
  });
};

const handleRefreshPage = () => {
  imageListPromise.value = new Promise((resolve, reject) => {
    getImages().then((imageListResult) => {
      imageList.value = imageListResult;
      resolve(imageListResult);
    });
  });
};

const handleConfirmAction = async () => {
  if (confirmFunction.value) {
    await confirmFunction.value();
  }
  showConfirmModal.value = false;
};

const confirmDuplicateUpload = async (args) => {
  confirmMessage.value = `Destination dataset contains ${args.duplicate_count} ${args.duplicate_count === 1 ? 'image' : 'images'} with the same name. 
      ${args.duplicate_count === 1 ? 'This image' : 'These images'} will be ignored.`;
  confirmMessageHeader.value = 'Duplicate files';
  buttonText.value = 'Confirm';
  buttonClass.value = null;
  confirmFunction.value = async () => { await handleConfirmDuplicateUpload(args.fullParams); };
};

const handleConfirmDuplicateUpload = async (params) => {
  // This will be accessed via template ref
};

const handleEditSequenceDescription = async (payload) => {
  const { sequenceId, description } = payload;
  try {
    const params = {
      sequence_id: sequenceId,
      description: description,
    };
    const resp = await dataConnect.updateSequenceDescription(params);
    if (resp && !resp.error) {
      const idx = seqList.value.findIndex((s) => s.id === sequenceId);
      if (idx >= 0) {
        seqList.value[idx].description = description;
      }
      toast.open({
        queue: true,
        duration: 3000,
        message: 'Description updated successfully',
        type: 'success',
        position: 'top',
      });
    } else {
      throw new Error(resp?.error?.message || 'Failed to update description');
    }
  } catch (error) {
    toast.open({
      queue: true,
      duration: 5000,
      message: `Error: ${error.message}`,
      type: 'error',
      position: 'top',
    });
  }
};

const handleSequenceDeleted = async (sequence) => {
  confirmMessage.value = `Are you sure you want to permanently delete this sequence?`;
  confirmMessageHeader.value = 'Deleting Sequence';
  buttonClass.value = 'button-delete';
  buttonText.value = 'Delete';
  confirmFunction.value = async () => {
    await deleteFilteredImages2([], [sequence.id]);
    selectedImages.value = [];
    selectAllPageFiles.value = false;
    selectAllFiles.value = false;
    await getSequences(dsID.value);
  };
  showConfirmModal.value = true;
};

const handleSequenceEdit = (payload) => {
  handleEditSequenceDescription(payload);
};

const handleSequenceDownload = (sequence) => {
  handleDownload(sequence);
};

const clearAllFilters = () => {
  // Use $reset if available (Pinia store), otherwise reset properties directly
  if (datasetFilters.value?.$reset) {
    datasetFilters.value.$reset();
  } else {
    datasetFilters.value.groupsFilter = [];
    datasetFilters.value.imageNameFilter = [];
    datasetFilters.value.timeRangeFilter = null;
    datasetFilters.value.dateRangeFilter = null;
    datasetFilters.value.cameraSourceFilter = [];
    datasetFilters.value.confidenceRangeFilter = null;
    datasetFilters.value.annotationTypeFilter = [];
    datasetFilters.value.annotationTypeFilterInvert = false;
    datasetFilters.value.annotationSetFilter = [];
    datasetFilters.value.imagesWithAnnotationSetFilter = [];
    datasetFilters.value.annotationLabelFilter = [];
    datasetFilters.value.boundingBoxSizeFilter = null;
    datasetFilters.value.imageTagFilter = [];
    datasetFilters.value.displayAnnotationsFilter = null;
  }
};

// Watchers
//MUID


// watch(() => route.query.dataset_id, async (newVal) => {
//   if (newVal) {
//     const idInt = parseInt(newVal);
//     await getDatasetDetail(idInt);
//   }
// });

// watch(() => route.query.dataset, async (newVal) => {
//   if (newVal && !route.query.dataset_id) {
//     const idInt = parseInt(newVal);
//     await getDatasetDetail(idInt);
//   }
// });

// watch(() => route.params.datasetId, async (newVal) => {
//   if (newVal) {
//     const idInt = parseInt(newVal, 16);
//     await getDatasetDetail(idInt);
//     currentDatasetID.value = currentDataset.value ? currentDataset.value.id : 0;
//   }
// });


// MUID
// watch(() => currentDataset.value, () => {
//   currentPage.value = 1;
//   currentDatasetID.value = currentDataset.value ? currentDataset.value.id : 0;
//   internalCurrentDataset.value = currentDataset.value;
// });

watch(
  () => combinedParams.value,
  async (newVal, oldVal) => {
    if (isInitializing.value) return;
    router.replace({
      name: route.name,
      query: {
        ...route.query,
        dataset: currentDatasetID.value,
        image_params: encodeURI(JSON.stringify({
          imagesPerPage: imagesPerPage.value,
          currentPage: currentPage.value > 0 ? currentPage.value : 1,
          sortBy: sortBy.value,
          reverse: reverse.value,
        })),
        filter_params: encodeURI(JSON.stringify(datasetFilters.value)),
        annotation_display: encodeURI(JSON.stringify(annotationDisplaySettings.value)),
        image_display: encodeURI(JSON.stringify(imageDisplaySettings.value)),
      },
      params: route.params,
    });
  },
  { deep: true }
);

// Watch for filter changes and apply them
watch(
  () => datasetFilters.value,
  async (newFilters) => {
    if (isInitializing.value) return;
    console.log('Filters changed:', newFilters);
    // Reset to page 1 when filters change
    currentPage.value = 1;
    // Get updated filtered count
    await getFilteredImageCount();
    // Re-fetch images with new filters
    if (activeTab.value === 'images') {
      await imageSelectedHandler();
    }
  },
  { deep: true }
);


//MUID

// watch(
//   () => getAnnotationSetsParams.value,
//   async (params) => {
//     if (params && params !== undefined) {
//       const annotationSetsResult = await getAnnotationSetList(params);
//       currentDataset.value = { ...currentDataset.value, annotation_sets: annotationSetsResult };
//     } else {
//       currentDataset.value = { ...currentDataset.value, annotation_sets: [] };
//     }
//   },
//   { deep: true }
// );

watch(
  () => activeTab.value,
  async (newTab) => {
    // Clear selections and close modals when switching tabs
    selectedImages.value = [];
    selectAllPageFiles.value = false;
    selectAllFiles.value = false;
    showCopyModal.value = false;

    if (newTab === 'sequences') {
      seqSelectedHandler();
    } else if (newTab === 'images') {
      await getFilteredImageCount();
      imageSelectedHandler();
    }
  }
);

watch(
  () => ({ imageCount: imageCount.value, seqCount: seqCount.value }),
  ({ imageCount: newImageCount, seqCount: newSeqCount }) => {
   // console.log('Checking if gallery is empty - imageCount:', newImageCount, 'seqCount:', newSeqCount);
    isEmpty.value = newImageCount === 0 && newSeqCount === 0;
   // console.log('Gallery is empty:', isEmpty.value);
  },
  { immediate: true }
);

// watch(
//   () => currentPage.value,
//   async (newPage) => {
//     if (activeTab.value === 'images') {
//       imageSelectedHandler();
//     }
//   }
// );

watch(
  () => imageList.value,
  (list) => {
    if (!hasSequences.value && list?.some((img) => img.type === 'sequence')) {
      hasSequences.value = true;
    }
  },
  { immediate: true }
);

watch(
  () => sequences.value,
  async (seqs) => {
    if (seqs && seqs.length > 0 && datasetExportModal.value) {
      // Open export modal when sequences are selected for download
      console.log('Opening export modal with sequences:', seqs);
      await nextTick();
      if (typeof datasetExportModal.value.showModal === 'function') {
        datasetExportModal.value.showModal();
      } else {
        console.warn('showModal method not found on export modal ref', datasetExportModal.value);
      }
    }
  },
  { deep: true }
);

watch(
  () => showCopyModal.value,
  async (show) => {
    if (show && galleryCopyModal.value) {
      // Open copy modal when triggered
      console.log('Opening copy modal');
      await nextTick();
      if (typeof galleryCopyModal.value.showModal === 'function') {
        galleryCopyModal.value.showModal();
      } else {
        console.warn('showModal method not found on copy modal ref', galleryCopyModal.value);
      }
    }
  },
);


</script>

<style lang="scss" scoped>
.gallery-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.gallery-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  position: relative;
  overflow: auto;
}
</style>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.gallery-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.gallery-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  position: relative;
}

.gallery-container.with-content {
  overflow: auto;
}

.gallery-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 20px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 10;
  
  p {
    margin: 0;
    font-size: 16px;
    color: #666;
  }
}

.lds-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;

  div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 64px;
    height: 64px;
    margin: 8px;
    border: 8px solid #1a1a1a;
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #1a1a1a transparent transparent transparent;

    &:nth-child(1) {
      animation-delay: -0.45s;
    }
    &:nth-child(2) {
      animation-delay: -0.3s;
    }
    &:nth-child(3) {
      animation-delay: -0.15s;
    }
  }
}

@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.controls-bar {
  display: flex;
  flex-direction: row;
  padding: 10px 20px 10px 20px;
  align-items: center;
  border-bottom: 1px solid #c9c9c9;
  @include themify() {
    background: themed('color-ribbon');
  }
  justify-content: space-between;
}

#dataset-select {
  width: 300px;
}

.gallery-tabs {
  display: flex;
  gap: 0;
  height: 100%;
  align-items: stretch;
}

.gallery-tab {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  border-bottom: 2px solid transparent;
  background: none;
  white-space: nowrap;
  @include themify() {
    color: themed('body-text-color-secondary');
  }

  &:hover {
    @include themify() {
      background: themed('card-list-hover-fill');
    }
  }

  &--active {
    border-bottom: 2px solid var(--color-primary);
    @include themify() {
      color: themed('body-text-color');
    }
  }
}

.gallery-filter-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  white-space: nowrap;
  @include themify() {
    color: themed('body-text-color');
  }

  &:hover {
    @include themify() {
      background: themed('card-list-hover-fill');
    }
  }

  &:active {
    opacity: 0.8;
  }
}

.gallery-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  height: 100%;
  width: 100%;
  justify-content: flex-end;

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__pagination {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 50px;
  }

  span{
    font-weight: bold;
    font-size: 1.15rem;
  }
}

.actions-dropdown-button {
  background: white;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 26px;
  padding: 0;
  box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
  padding: 4px 8px;
  gap: 2px;
  width: fit-content;
  border: none;
  cursor: pointer;

  @include themify() {
    color: themed('body-text-color');
  }

  &:hover {
    background: rgba(var(--color-primary-100-rgb), 0.5);
  }

  &.delete:not([disabled]) {
    @include themify() {
      background: themed('button-delete');
      color: themed('button-delete-text');
    }
  }

  &.delete[disabled] {
    border: none;
  }

  &.delete:hover:not([disabled]) {
    @include themify() {
      background: $color-red-300;
      color: themed('button-delete-text');
    }
  }
}

.actions-toggle-button {
  display: flex;
  align-items: center;
  height: 100%;
  flex: 1 1 auto;
  padding: 4px 8px;
  gap: 2px;
  border-right: solid 1px rgba(0,0,0,0.5);
  color: inherit;
  background: white;

  &:hover {
    box-shadow: 0 0 2px 1px rgba(var(--color-primary-rgb), 0.2) inset;
    background: rgba(var(--color-primary-100-rgb), 0.5);
  }

  &:active {
    opacity: 1;
  }

  &.active {
    @include themify() {
      box-shadow: 0 0 2px 1px rgba(var(--color-primary-rgb), 0.5) inset;
      background: rgba(var(--color-primary-100-rgb), 0.8);
    }
  }
}

.actions-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 10px;
  background: #e53935;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.sort-by {
  display: flex;
  flex-direction: row;
  align-items: center;

  &__select {
    width: 130px;
  }

  &__reverse._invert {
    transform: rotate(180deg);
  }
}

#loading-visualization {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  background: transparent;
}

.gallery-sequence-loading {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  z-index: 101;
  background: transparent;

  p {
    margin: 0;
    font-size: 16px;
    color: #666;
  }
}

.gallery-image-loading {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  z-index: 100;
  background: transparent;

  p {
    margin: 0;
    font-size: 16px;
    color: #666;
  }
}

.lds-ring {
  /* change color here */
  color: var(--color-primary);
}
.lds-ring,
.lds-ring div {
  box-sizing: border-box;
}
.lds-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid currentColor;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: currentColor transparent transparent transparent;
}
.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

:deep(#gallery-page-selector button) {
  min-width: fit-content;
}

.gallery-loading-placeholder {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 60px 20px;

  p {
    margin: 0;
    font-size: 14px;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }
}

.gallery-empty-placeholder {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 300px;

  p {
    margin: 0;
    font-size: 16px;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }
}

.select-all-warning {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50px;
  background: var(--gray-300);
  color: var(--gray-600);
  font-weight: 400;
  gap: 12px;

  button {
    color: var(--button-link-color);
  }
}

.filter-drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 500;
  display: flex;
  justify-content: flex-end;
}

.filter-drawer {
  width: 300px;
  max-width: 100vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
  background: #f5f5f5;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #c9c9c9;
    flex-shrink: 0;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__count {
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #666;
    font-weight: normal;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;

    :deep(.filters) {
      flex-direction: column;
      flex-wrap: nowrap;
    }

    :deep(.filters__active-and-default-filters) {
      flex-direction: column;
      flex-wrap: nowrap;

      > * {
        width: 100%;
      }
    }

    :deep(.filter-button) {
      width: 100% !important;
      max-width: 100% !important;
    }
  }
}

.clear-filters-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #999;
  border-radius: 4px;
  background: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #e8e8e8;
    border-color: #666;
  }

  &:active {
    opacity: 0.8;
  }
}

.filter-drawer-enter-active,
.filter-drawer-leave-active {
  transition: opacity 0.25s ease;

  .filter-drawer {
    transition: transform 0.25s ease;
  }
}

.filter-drawer-enter-from,
.filter-drawer-leave-to {
  opacity: 0;

  .filter-drawer {
    transform: translateX(100%);
  }
}

@media (max-width: 480px) {
  .filter-drawer {
    width: 100vw;
  }
}
</style>
